/**
 * Parse version string as timestamp (format: YYYYMMDDHHmmss) and return time ago
 * Returns null if version is not parsable as timestamp
 */
export function parseVersionTimestamp(version: string): Date | null {
  try {
    // Check if version matches timestamp format (14 digits)
    if (!/^\d{14}$/.test(version)) {
      return null;
    }

    const year = parseInt(version.substring(0, 4));
    const month = parseInt(version.substring(4, 6)) - 1; // JS months are 0-indexed
    const day = parseInt(version.substring(6, 8));
    const hour = parseInt(version.substring(8, 10));
    const minute = parseInt(version.substring(10, 12));
    const second = parseInt(version.substring(12, 14));

    const date = new Date(year, month, day, hour, minute, second);
    
    // Validate the date is valid
    if (isNaN(date.getTime())) {
      return null;
    }

    return date;
  } catch {
    return null;
  }
}

/**
 * Format time difference in human-readable format
 */
export function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  } else if (diffHours < 10) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return 'more than 10 hours ago';
  } else if (diffDays < 30) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  } else {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months !== 1 ? 's' : ''} ago`;
  }
}

/**
 * Get time ago string from version, returns null if not parsable
 */
export function getVersionAge(version: string): string | null {
  const timestamp = parseVersionTimestamp(version);
  if (!timestamp) {
    return null;
  }
  return formatTimeAgo(timestamp);
}
